import { api } from './api';

/* Mirrors Eduvora.Application.QuestionBank — MarkEntryService. */

export interface MarkColumn {
  itemId: string; questionId: string; sectionLabel: string; number: number;
  isAlternative: boolean; marks: number; type: string; preview: string;
  unitId: string | null; unitName: string | null;
  /** For an MCQ, the correct letter — so a section can be marked by typing what the child chose. */
  correctOption: string | null;
  optionLabels: string[];
}

export interface MarkCell { itemId: string; marks: number; attempted: boolean }

export interface MarkRow {
  studentId: string; fullName: string; admissionNumber: string; rollNumber: string | null;
  cells: MarkCell[]; total: number; entered: number; skipped: number;
}

export interface ClassOption { id: string; label: string; students: number }

export interface MarkSheet {
  paperId: string; paperTitle: string; subjectName: string; classLabel: string | null;
  academicClassId: string | null; status: string; totalMarks: number;
  columns: MarkColumn[]; rows: MarkRow[]; classes: ClassOption[];
  captured: number; expected: number; note: string | null;
}

export interface MasteryUnitView {
  unitId: string | null; unitName: string; percent: number; marksAvailable: number;
  marksScored: number; questionsAttempted: number; questionsSkipped: number;
  /** "TooLittleEvidence" | "Indicative" | "Solid" */
  confidence: string;
  classPercent: number | null; versusClass: number | null;
}

export interface MasteryClassUnitView {
  unitId: string | null; unitName: string; percent: number; marksAvailable: number;
  /** ⚠ Median marks ONE child faced. `marksAvailable` is the class-wide sum, forty times larger,
      and `confidence` is judged on THIS number — never print the verdict beside the other one. */
  marksPerChild: number;
  studentsBelowHalf: number; studentsAnalysed: number; confidence: string;
}

export interface MasterySliceView { name: string; percent: number; marksAvailable: number }

export interface MasteryStudentView {
  studentId: string; fullName: string; admissionNumber: string;
  overallPercent: number; marksScored: number; marksAvailable: number; skipped: number;
  headline: string;
  units: MasteryUnitView[]; weakest: MasteryUnitView[]; strongest: MasteryUnitView[];
}

export interface MasteryReport {
  paperId: string; paperTitle: string; subjectName: string; classLabel: string | null;
  overallPercent: number; studentsAnalysed: number;
  units: MasteryClassUnitView[];
  teachingSignals: MasteryClassUnitView[];
  typology: MasterySliceView[];
  difficulty: MasterySliceView[];
  students: MasteryStudentView[];
  note: string | null;
}

export const marksApi = {
  sheet: (paperId: string, academicClassId?: string) =>
    api.get<MarkSheet>(`/api/marks/sheet?${new URLSearchParams({
      paperId, ...(academicClassId ? { academicClassId } : {}),
    })}`),

  /* ⚠ One save per child, not per cell. A teacher marks one script at a time, and a request on
     every keystroke is a request that fails halfway through a script on a school's wifi. */
  save: (paperId: string, studentId: string, cells: MarkCell[]) =>
    api.post<{ saved: number }>('/api/marks', { paperId, studentId, cells }),

  mastery: (paperId: string, academicClassId?: string, studentId?: string) =>
    api.get<MasteryReport>(`/api/marks/mastery?${new URLSearchParams({
      paperId,
      ...(academicClassId ? { academicClassId } : {}),
      ...(studentId ? { studentId } : {}),
    })}`),
};

/**
 * Green above this, amber above the next, red below. Mastery, not marks — the bands differ.
 *
 * ⚠ PASS THE CONFIDENCE. A unit with too little evidence gets NO colour at all, because a red
 * 36% on four marks and a red 31% on sixty marks are not the same finding and were rendering
 * identically — same red, same full bar, one row apart. Colour is a claim; if the calculator has
 * refused to draw a conclusion the screen must not draw one on its behalf.
 *
 * ⚠ PASS AN ALREADY-ROUNDED PERCENTAGE. 64.7 prints as "65%" and bands as amber, so the number
 * and its colour contradict each other at every boundary. Round once, then tone the rounded value.
 */
export function masteryTone(pct: number, confidence?: string): 'ok' | 'warn' | 'bad' | 'none' {
  if (confidence === 'TooLittleEvidence') return 'none';
  if (pct >= 65) return 'ok';
  if (pct >= 45) return 'warn';
  return 'bad';
}

/** ⚠ Never render a percentage without saying how much evidence is behind it. */
export function confidenceLabel(c: string, marks: number): string {
  const m = `${marks} ${marks === 1 ? 'mark' : 'marks'}`;
  if (c === 'TooLittleEvidence') return `only ${m} — too little to act on`;
  if (c === 'Indicative') return `${m} — indicative`;
  return m;
}

/**
 * The same sentence for a CLASS row, and the number is deliberately different.
 *
 * ⚠ A class row carries two marks figures and only one of them means anything here. The verdict
 * is computed from the median marks a single child faced; the class-wide total is that multiplied
 * by the number of children. Printing "456 marks — too little to act on" is a sentence that
 * argues with itself, and it was on the screen.
 */
export function classEvidenceLabel(c: string, marksPerChild: number): string {
  const m = `${marksPerChild} ${marksPerChild === 1 ? 'mark' : 'marks'} per child`;
  if (c === 'TooLittleEvidence') return `only ${m} — too little to act on`;
  if (c === 'Indicative') return `${m} — indicative`;
  return m;
}
